import React from 'react';
import {
  Users,
  Coins,
  Pickaxe,
  Star,
  Share2,
  Tv,
  CheckCircle2,
  Info,
  TrendingUp,
  Flame,
  Zap,
  ArrowUpRight,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import type { AdminStatsResponse } from '@nova-mine/shared';
import type { AdminTab } from './AdminHeader.js';

interface DashboardViewProps {
  stats: AdminStatsResponse;
  onNavigate: (tab: AdminTab) => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({ stats, onNavigate }) => {
  return (
    <div className="space-y-6">
      {/* Activity Definition Banner */}
      <div className="glass-panel p-4 rounded-2xl border border-purple-500/20 bg-purple-950/20 flex items-start space-x-3">
        <Info className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-purple-200">
          <span className="font-bold text-white">Documented Activity Standard: </span>
          <span>{stats.active_users.activity_definition}</span>
          <span className="block mt-0.5 text-purple-300 font-mono text-[11px]">
            Active: {stats.active_users.d1.toLocaleString()} users (24h) • {stats.active_users.d7.toLocaleString()} users (7d)
          </span>
        </div>
      </div>

      {/* Row 1: Primary Metrics (Users, Coins Available, Coins Claimed, Stars Revenue) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="glass-panel p-5 rounded-2xl border border-white/10 hover:border-cyan-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-400">Total Registered</span>
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-2xl font-black text-white">
            {stats.total_users.toLocaleString()}
          </div>
          <div className="mt-2 text-[11px] text-gray-400 flex items-center justify-between">
            <span>24h Active:</span>
            <span className="font-bold text-cyan-400">{stats.active_users.d1.toLocaleString()}</span>
          </div>
          <div className="text-[11px] text-gray-400 flex items-center justify-between">
            <span>7d Active:</span>
            <span className="font-bold text-purple-400">{stats.active_users.d7.toLocaleString()}</span>
          </div>
        </div>

        {/* Circulating / Available Coins */}
        <div className="glass-panel p-5 rounded-2xl border border-white/10 hover:border-nova-gold/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-400">Coins Available</span>
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-nova-gold flex items-center justify-center">
              <Coins className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-2xl font-black text-nova-gold font-mono">
            {stats.total_coins_available.toLocaleString()}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Total currently held in user balances across platform
          </p>
        </div>

        {/* Total Claimed Coins */}
        <div className="glass-panel p-5 rounded-2xl border border-white/10 hover:border-emerald-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-400">Total Coins Claimed</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 text-2xl font-black text-emerald-400 font-mono">
            {stats.total_coins_claimed.toLocaleString()}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Historical lifetime mining claims accrued & crystallized
          </p>
        </div>

        {/* Telegram Stars Revenue */}
        <div className="glass-panel p-5 rounded-2xl border border-white/10 hover:border-amber-400/30 transition-all bg-gradient-to-b from-amber-500/5 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-400">Stars Revenue</span>
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-nova-gold flex items-center justify-center">
              <Star className="w-4 h-4 fill-nova-gold" />
            </div>
          </div>
          <div className="mt-3 text-2xl font-black text-white flex items-center space-x-1.5 font-mono">
            <Star className="w-5 h-5 fill-nova-gold text-nova-gold" />
            <span>{stats.stars_orders.total_stars_revenue.toLocaleString()} Stars</span>
          </div>
          <div className="mt-2 text-[11px] text-gray-400 flex items-center justify-between">
            <span>Settled Orders:</span>
            <span className="font-bold text-emerald-400">{stats.stars_orders.paid_orders} paid</span>
          </div>
        </div>
      </div>

      {/* Row 2: Subsystem Breakdown (Miners, Referrals, AdsGram, Tasks, Stars) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Active Miners Breakdown Card */}
        <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-white flex items-center space-x-2">
              <Pickaxe className="w-4 h-4 text-purple-400" />
              <span>Mining Fleets</span>
            </h3>
            <button
              onClick={() => onNavigate('miners')}
              className="text-xs font-bold text-purple-400 hover:text-purple-300 flex items-center space-x-1"
            >
              <span>Manage</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="w-3 h-3 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50" />
                <span className="text-xs text-gray-300">Active Main Miners</span>
              </div>
              <span className="text-sm font-mono font-bold text-white">
                {stats.active_miners.main.toLocaleString()}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="w-3 h-3 rounded-full bg-purple-400 shadow-sm shadow-purple-400/50" />
                <span className="text-xs text-gray-300">Active Auto Miners</span>
              </div>
              <span className="text-sm font-mono font-bold text-white">
                {stats.active_miners.auto.toLocaleString()}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-space-900 border border-amber-500/20 flex items-center justify-between bg-amber-500/5">
              <div className="flex items-center space-x-2.5">
                <Star className="w-3.5 h-3.5 fill-nova-gold text-nova-gold" />
                <span className="text-xs font-bold text-nova-gold">Active Stars Rigs</span>
              </div>
              <span className="text-sm font-mono font-bold text-nova-gold">
                {stats.active_miners.stars_total_units.toLocaleString()} units
              </span>
            </div>
          </div>
        </div>

        {/* AdsGram & Website Tasks Card */}
        <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-white flex items-center space-x-2">
              <Tv className="w-4 h-4 text-cyan-400" />
              <span>AdsGram & Quests</span>
            </h3>
            <button
              onClick={() => onNavigate('activity')}
              className="text-xs font-bold text-cyan-400 hover:text-cyan-300 flex items-center space-x-1"
            >
              <span>View Logs</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
              <span className="text-gray-400">Total Ad Attempts:</span>
              <span className="font-bold text-white font-mono">
                {stats.adsgram.total_attempts.toLocaleString()}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
                <span className="block text-emerald-400 font-black text-sm">
                  {stats.adsgram.completed.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-400">Completed (Rewarded)</span>
              </div>
              <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-center">
                <span className="block text-red-400 font-black text-sm">
                  {stats.adsgram.failed.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-400">Failed / Skipped</span>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
              <span className="text-gray-400">Website Task Completions:</span>
              <span className="font-bold text-cyan-400 font-mono">
                {stats.website_tasks.total_completed.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Referrals & Orders Health Card */}
        <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-white flex items-center space-x-2">
              <Share2 className="w-4 h-4 text-amber-400" />
              <span>Referrals & Orders</span>
            </h3>
            <button
              onClick={() => onNavigate('payments')}
              className="text-xs font-bold text-nova-gold hover:underline flex items-center space-x-1"
            >
              <span>Orders</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
              <span className="text-gray-400">Eligible Attributions:</span>
              <span className="font-bold text-white font-mono">
                {stats.referrals.total_eligible.toLocaleString()}
              </span>
            </div>

            <div className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
              <span className="text-gray-400">Referral Rewards Issued:</span>
              <span className="font-bold text-nova-gold font-mono">
                +{stats.referrals.total_rewards_issued.toLocaleString()} NOVA
              </span>
            </div>

            <div className="pt-2 border-t border-white/5 grid grid-cols-3 gap-1.5 text-center text-xs">
              <div className="p-1.5 rounded-lg bg-space-900">
                <span className="text-emerald-400 font-bold block">{stats.stars_orders.paid_orders}</span>
                <span className="text-[10px] text-gray-500">Paid</span>
              </div>
              <div className="p-1.5 rounded-lg bg-space-900">
                <span className="text-amber-400 font-bold block">{stats.stars_orders.pending}</span>
                <span className="text-[10px] text-gray-500">Pending</span>
              </div>
              <div className="p-1.5 rounded-lg bg-space-900">
                <span className="text-gray-400 font-bold block">{stats.stars_orders.cancelled}</span>
                <span className="text-[10px] text-gray-500">Cancelled</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
